import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowDown, Calendar, Megaphone, Star, Users } from "lucide-react";
import { PageHero, Reveal, SectionHeading } from "@/components/motion";
import { API_URL, CampForm, sessionDateLabel } from "@/components/forms";

const CAMPS = [
  {
    key: "showcase",
    icon: Star,
    badge: "For post-grad athletes",
    title: "Post-Grad Showcase Camp",
    text: "Built for athletes taking — or considering — a post-graduate year: combine testing, position drills, film review and live scrimmage in front of our staff.",
    testid: "camp-card-showcase",
  },
  {
    key: "highschool",
    icon: Megaphone,
    badge: "HS juniors & seniors",
    title: "High School Camp",
    text: "A college-style workout experience for high school juniors and seniors: drills, competition, film and an honest evaluation of where you stand right now.",
    testid: "camp-card-highschool",
  },
  {
    key: "community",
    icon: Users,
    badge: "Tuskegee community",
    title: "Community Camp",
    text: "Held right here in Tuskegee to build ties within our community — skills work, teamwork and mentorship for local athletes, Bulldog style.",
    testid: "camp-card-community",
  },
];

export default function CampsPage() {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");

  useEffect(() => {
    axios
      .get(`${API_URL}/camps/sessions`)
      .then((r) => setSessions(r.data || []))
      .catch(() => {});
  }, []);

  const reserve = (s) => {
    setSelectedSessionId(s.id);
    document.querySelector("#camp-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <PageHero
        testid="camps-hero"
        eyebrow="Camps · Tuskegee, Alabama"
        lines={[<>Come work.</>, <span className="text-ice">Get seen.</span>]}
        sub="We host camps for post-graduate students as well as high school juniors and seniors — plus community camps held in Tuskegee to build ties within the community. Book a dated session below or register general interest."
      />

      {/* CAMP CARDS */}
      <section className="border-t border-white/5 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {CAMPS.map((c, i) => {
              const mine = sessions.filter((s) => s.camp_key === c.key);
              return (
                <Reveal key={c.testid} delay={i * 0.1} className="h-full">
                  <div
                    data-testid={c.testid}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-navy-800 p-8 transition-colors hover:border-ice/50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="grid h-12 w-12 place-items-center rounded-xl border border-ice/30 bg-ice/10 text-ice transition-colors group-hover:bg-ice group-hover:text-navy-900">
                        <c.icon className="h-6 w-6" />
                      </div>
                      <span className="rounded-full border border-crimson/40 bg-crimson/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-crimson-bright">
                        {c.badge}
                      </span>
                    </div>
                    <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-wide text-white">
                      {c.title}
                    </h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-300">{c.text}</p>

                    {mine.length > 0 ? (
                      <div className="mt-6 space-y-3 border-t border-white/5 pt-5" data-testid={`camp-sessions-${c.key}`}>
                        {mine.map((s) => (
                          <div
                            key={s.id}
                            data-testid={`camp-session-row-${s.id}`}
                            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-navy-700 px-4 py-3"
                          >
                            <div>
                              <p
                                data-testid={`camp-session-date-${s.id}`}
                                className="flex items-center gap-2 font-display text-base font-bold uppercase tracking-wide text-white"
                              >
                                <Calendar className="h-4 w-4 text-ice" />
                                {sessionDateLabel(s)}
                              </p>
                              <p
                                data-testid={`camp-session-spots-${s.id}`}
                                className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400"
                              >
                                {s.capacity > 0 ? `${s.spots_left} of ${s.capacity} spots left` : "Open capacity"}
                              </p>
                            </div>
                            <button
                              data-testid={`camp-reserve-btn-${s.id}`}
                              onClick={() => reserve(s)}
                              className="btn-primary !px-5 !py-2.5 !text-sm"
                            >
                              Reserve
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-6 border-t border-white/5 pt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                        2027 dates — announced soon
                      </p>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1} className="mt-14">
            <a
              href="#camp-form"
              data-testid="camps-scroll-to-form"
              className="link-underline inline-flex items-center gap-2 font-display text-base font-bold uppercase tracking-wider text-ice"
            >
              Register below <ArrowDown className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* REGISTRATION FORM */}
      <section
        id="camp-form"
        data-testid="camp-form-section"
        className="border-t border-white/5 bg-navy-850 py-24 sm:py-28"
      >
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <SectionHeading
            align="center"
            eyebrow="Camp registration"
            title={
              <>
                Book your <span className="text-ice">session.</span>
              </>
            }
            description="Pick a dated session and reserve your spot — or register general interest and our staff contacts you first when your camp opens up."
          />
          <Reveal delay={0.2} className="mt-12">
            <div className="glass rounded-2xl p-6 sm:p-8">
              <CampForm sessions={sessions} selectedSessionId={selectedSessionId} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
